import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	FormHelperText,
	Input,
	InputLabel,
	MenuItem,
	Select,
	Typography,
} from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";
import { Button, COLORS, Modal } from "abs-ui-components";

const CiiTargetsActuals = ({ ciiTargetsActuals, ciiTargets, refetchMonitoringData, dropdownData, vesselMstId }) => {
	const [validationErrors, setValidationErrors] = useState({});
	const [fuelConsData, setFuelConsData] = useState([]);
	const [selectedBaseYear, setSelectedBaseYear] = useState(new Date().getFullYear());
	const [availableYears, setAvailableYears] = useState([]);
	const [isDirty, setIsDirty] = useState(false);
	const [requiredCIIValues, setRequiredCIIValues] = useState({});
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const handleSaveClick = () => {
		if (ciiTargets && ciiTargets.length > 0 && ciiTargets[0].year && selectedBaseYear && ciiTargets[0].year !== selectedBaseYear) {
			setOpenConfirmDialog(true);
		} else {
			handleSave();
		}
	};
	const handleConfirmSave = async () => {
		setOpenConfirmDialog(false);
		await handleSave();
		try {
			await callApi({
				method: "POST",
				url: API_ROUTES.SeempIII43(vesselMstId),
				data: [],
			});
		} catch (err) {
			console.error("Error calling SeempIII43:", err);
		}
	};
	const handleCancelSave = () => {
		setOpenConfirmDialog(false);
	};
	useEffect(() => {
		const fetchRequiredCII = async () => {
			try {
				const response = await callApi({
					url: API_ROUTES.SYS_CII_VALUES(vesselMstId, selectedBaseYear),
					method: "GET",
					data: {},
				});
				if (response) {
					const ciiMap = response.reduce((acc, item) => {
						acc[item.year] = item.requiredCii;
						return acc;
					}, {});
					setRequiredCIIValues(ciiMap);
				} else {
					console.error("required Cii returned no data", response);
				}
			} catch (error) {
				console.error("Error fetching data", error);
			}
		};

		fetchRequiredCII();
	}, [vesselMstId, selectedBaseYear]);

	useEffect(() => {
		if (dropdownData?.years) {
			const years = dropdownData.years.map((item) => parseInt(item.value)).filter((year) => !isNaN(year));
			setAvailableYears(years);
			if (ciiTargetsActuals && ciiTargetsActuals.length > 0) {
				const yearsWithData = ciiTargetsActuals
					.filter((item) => item.preCorrectionAttainedCII || item.attainedAnnualCII || item.carbonIntensityRating)
					.map((item) => item.year);

				if (yearsWithData.length > 0) {
					const maxYearWithData = Math.max(...yearsWithData);
					setSelectedBaseYear(maxYearWithData + 1);
					return;
				}
			}

			if (years.length > 0) {
				const currentYear = new Date().getFullYear();
				const closestYear = years.reduce((prev, curr) => (Math.abs(curr - currentYear) < Math.abs(prev - currentYear) ? curr : prev));
				setSelectedBaseYear(closestYear);
			}
		} else {
			const currentYear = new Date().getFullYear();
			const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
			setAvailableYears(years);
			setSelectedBaseYear(currentYear);
		}
	}, [dropdownData, ciiTargetsActuals]);

	const generateYearRange = (baseYear) => {
		const currentYear = new Date().getFullYear();
		const endYear = 2030;
		const previousYears = Array.from({ length: 3 }, (_, i) => baseYear - (i + 1)).reverse();
		const futureYears = [];
		for (let year = baseYear; year <= endYear; year++) {
			futureYears.push(year);
		}
		return [...previousYears, ...futureYears];
	};

	useEffect(() => {
		const yearRange = generateYearRange(selectedBaseYear);
		if (Array.isArray(ciiTargetsActuals) && ciiTargetsActuals.length > 0) {
			const existingDataMap = new Map(ciiTargetsActuals.map((item) => [item.year, item]));
			const newData = yearRange.map((year) => {
				const existingItem = existingDataMap.get(year);
				return existingItem
					? {
							year,
							requiredAnnualCII: String(requiredCIIValues[year] || ""),
							preCorrectionAttainedCII: existingItem.preCorrectionAttainedCII || "",
							attainedAnnualCII: existingItem.attainedAnnualCII || "",
							carbonIntensityRating: existingItem.carbonIntensityRating || "",
					  }
					: {
							year,
							requiredAnnualCII: String(requiredCIIValues[year] || ""),
							preCorrectionAttainedCII: "",
							attainedAnnualCII: "",
							carbonIntensityRating: "",
					  };
			});

			setFuelConsData(newData);
		} else {
			setFuelConsData(
				yearRange.map((year) => ({
					year,
					requiredAnnualCII: String(requiredCIIValues[year] || ""),
					preCorrectionAttainedCII: "",
					attainedAnnualCII: "",
					carbonIntensityRating: "",
				}))
			);
		}
	}, [selectedBaseYear, ciiTargetsActuals, requiredCIIValues]);

	const validateRow = (row) => {
		const errors = {};
		if (row.year < selectedBaseYear) {
			if (!row.preCorrectionAttainedCII) {
				errors.preCorrectionAttainedCII = "Field is required";
			}
			if (!row.attainedAnnualCII) {
				errors.attainedAnnualCII = "Field is required";
			}
			if (!row.carbonIntensityRating) {
				errors.carbonIntensityRating = "Field is required";
			}
		}

		return errors;
	};

	const handleSave = async () => {
		const errors = {};

		for (let index = 0; index < fuelConsData.length; index++) {
			const row = fuelConsData[index];
			const rowErrors = validateRow(row);
			if (Object.keys(rowErrors).length > 0) {
				errors[index] = rowErrors;
			}
		}

		if (Object.keys(errors).length > 0) {
			setValidationErrors(errors);
			return;
		}
		try {
			const payload = fuelConsData.map((item, index) => {
				if (item.year < selectedBaseYear) {
					return {
						...item,
						index,
						vesselMstId: parseInt(vesselMstId),
					};
				} else {
					return {
						year: item.year,
						requiredAnnualCII: item.requiredAnnualCII,
						preCorrectionAttainedCII: "",
						attainedAnnualCII: "",
						carbonIntensityRating: "",
						index,
						vesselMstId: parseInt(vesselMstId),
					};
				}
			});

			const resp = await callApi({
				method: "POST",
				url: API_ROUTES.SeempIII22(vesselMstId),
				data: payload,
			});

			if (resp?.isSuccess) {
				emitToasterMsg("success", "Table Required CII over next three years", "Updated successfully");
				refetchMonitoringData?.();
				setIsDirty(false);
			} else {
				emitToasterMsg("error", "Table Required CII over next three years", resp?.errorMessage || "Failed to update");
			}
		} catch (err) {
			console.error("Error saving data:", err);
			emitToasterMsg("error", "Table Required CII over next three years", err?.message || "Error saving data");
		}
	};

	const handleCellChange = (rowIndex, columnId, value) => {
		setFuelConsData((prev) => {
			const newData = [...prev];
			newData[rowIndex] = {
				...newData[rowIndex],
				[columnId]: value,
			};
			return newData;
		});
		setValidationErrors((prev) => {
			const newErrors = { ...prev };
			if (newErrors[rowIndex]) {
				delete newErrors[rowIndex][columnId];
				if (Object.keys(newErrors[rowIndex]).length === 0) {
					delete newErrors[rowIndex];
				}
			}
			return newErrors;
		});
		setIsDirty(true);
	};

	const columns = useMemo(() => {
		const shouldShowColumns = (year) => year < selectedBaseYear;

		const baseColumns = [
			{
				accessorKey: "year",
				header: "Year",
				size: 80,
				enableEditing: false,
				muiTableBodyCellProps: {
					sx: {
						borderRight: "1px solid rgba(224, 224, 224, 1)",
						height: "45px",
						padding: "8px",
					},
				},
			},
			{
				accessorKey: "requiredAnnualCII",
				header: "Required annual operational CII",
				size: 120,
				enableEditing: false,
				Cell: ({ cell }) => (
					<Typography
						variant="body2"
						sx={{
							padding: "8px",
							color: cell.getValue() ? "inherit" : "text.disabled",
						}}
					>
						{cell.getValue() || ""}
					</Typography>
				),
				muiTableBodyCellProps: {
					sx: {
						borderRight: "1px solid rgba(224, 224, 224, 1)",
						height: "45px",
						padding: "0 8px",
					},
				},
			},
		];

		const additionalColumns = [
			{
				accessorKey: "preCorrectionAttainedCII",
				header: "Attained annual CII (pre-correction)",
				size: 120,
				Cell: ({ cell, row }) =>
					shouldShowColumns(row.original.year) ? (
						<FormControl fullWidth variant="standard" error={!!validationErrors[row.index]?.preCorrectionAttainedCII}>
							<Input
								type="text"
								value={cell.getValue()}
								onChange={(e) => handleCellChange(row.index, cell.column.id, e.target.value)}
								sx={{
									width: "100%",
									height: "100%",
									"&:before": {
										borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
									},
									"&:hover:not(.Mui-disabled):before": {
										borderBottom: "2px solid rgba(0, 0, 0, 0.87)",
									},
									"&:after": {
										borderBottom: "2px solid #1976d2",
									},
								}}
							/>
							{validationErrors[row.index]?.preCorrectionAttainedCII && (
								<FormHelperText>{validationErrors[row.index]?.preCorrectionAttainedCII}</FormHelperText>
							)}
						</FormControl>
					) : (
						<Box sx={{ height: "100%" }} />
					),
				muiTableBodyCellProps: ({ row }) => ({
					sx: {
						borderRight: shouldShowColumns(row.original.year) ? "1px solid rgba(224, 224, 224, 1)" : "none",
						borderBottom: shouldShowColumns(row.original.year) ? "1px solid rgba(224, 224, 224, 1)" : "none",
						height: "45px",
						padding: shouldShowColumns(row.original.year) ? "0 8px" : "0",
						backgroundColor: shouldShowColumns(row.original.year) ? "inherit" : "transparent",
						"&:hover": {
							backgroundColor: shouldShowColumns(row.original.year) ? "rgba(0, 0, 0, 0.04)" : "transparent",
						},
					},
				}),
			},
			{
				accessorKey: "attainedAnnualCII",
				header: "Attained annual operational CII",
				size: 120,
				Cell: ({ cell, row }) =>
					shouldShowColumns(row.original.year) ? (
						<FormControl fullWidth variant="standard" error={!!validationErrors[row.index]?.attainedAnnualCII}>
							<Input
								type="text"
								value={cell.getValue()}
								onChange={(e) => handleCellChange(row.index, cell.column.id, e.target.value)}
								sx={{
									width: "100%",
									height: "100%",
									"&:before": {
										borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
									},
									"&:hover:not(.Mui-disabled):before": {
										borderBottom: "2px solid rgba(0, 0, 0, 0.87)",
									},
									"&:after": {
										borderBottom: "2px solid #1976d2",
									},
								}}
							/>
							{validationErrors[row.index]?.attainedAnnualCII && (
								<FormHelperText>{validationErrors[row.index]?.attainedAnnualCII}</FormHelperText>
							)}
						</FormControl>
					) : (
						<Box sx={{ height: "100%" }} />
					),
				muiTableBodyCellProps: ({ row }) => ({
					sx: {
						borderRight: shouldShowColumns(row.original.year) ? "1px solid rgba(224, 224, 224, 1)" : "none",
						borderBottom: shouldShowColumns(row.original.year) ? "1px solid rgba(224, 224, 224, 1)" : "none",
						height: "45px",
						padding: shouldShowColumns(row.original.year) ? "0 8px" : "0",
						"&:hover": {
							backgroundColor: shouldShowColumns(row.original.year) ? "rgba(0, 0, 0, 0.04)" : "transparent",
						},
					},
				}),
			},
			{
				accessorKey: "carbonIntensityRating",
				header: "Carbon Intensity Rating",
				size: 120,
				Cell: ({ cell, row }) => {
					if (!shouldShowColumns(row.original.year)) return <Box sx={{ height: "100%" }} />;

					const options =
						dropdownData?.ratings?.map((rating) => ({
							value: rating.code || rating.value || rating,
							label: rating.value || rating.code || rating,
						})) || ["A", "B", "C", "D", "E", "Not Applicable"].map((r) => ({ value: r, label: r }));

					return (
						<Box>
							<Select
								value={cell.getValue() || ""}
								onChange={(e) => handleCellChange(row.index, cell.column.id, e.target.value)}
								displayEmpty
								fullWidth
								variant="standard"
								error={!!validationErrors[row.index]?.carbonIntensityRating}
								sx={{
									minWidth: 130, 
									"&:before": {
										borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
									},
									"&:hover:not(.Mui-disabled):before": {
										borderBottom: "2px solid rgba(0, 0, 0, 0.87)",
									},
									"&:after": {
										borderBottom: "2px solid #1976d2",
									},
								}}
								MenuProps={{
									disableScrollLock: true,
									PaperProps: {
										sx: {
											maxHeight: 300,
										},
									},
								}}
							>
								{options.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</Select>
							{validationErrors[row.index]?.carbonIntensityRating && (
								<Typography variant="caption" color="error" sx={{ ml: 1 }}>
									{validationErrors[row.index]?.carbonIntensityRating}
								</Typography>
							)}
						</Box>
					);
				},
				muiTableBodyCellProps: ({ row }) => ({
					sx: {
						borderRight: shouldShowColumns(row.original.year) ? "1px solid rgba(224, 224, 224, 1)" : "none",
						borderBottom: shouldShowColumns(row.original.year) ? "1px solid rgba(224, 224, 224, 1)" : "none",
						height: "45px",
						padding: shouldShowColumns(row.original.year) ? "0 8px" : "0",
						"&:hover": {
							backgroundColor: shouldShowColumns(row.original.year) ? "rgba(0, 0, 0, 0.04)" : "transparent",
						},
					},
				}),
			},
		];

		return [...baseColumns, ...additionalColumns];
	}, [dropdownData, selectedBaseYear, validationErrors]);
	const table = useMaterialReactTable({
		columns,
		data: fuelConsData || [],
		enableStickyHeader: true,
		enableSorting: false,
		enableRowSelection: false,
		enableColumnPinning: false,
		enableTopToolbar: false,
		enableClickToEdit: true,
		enableColumnActions: false,
		enablePagination: false,
		muiTableHeadCellProps: {
			sx: {
				color: "white",
				backgroundColor: "#042a4c",
				borderRight: "1px solid lightgray",
				height: "45px",
			},
		},
		muiTableBodyCellProps: {
			sx: {
				borderRight: "1px solid rgba(224, 224, 224, 1)",
				borderBottom: "1px solid rgba(224, 224, 224, 1)",
				height: "35px",
				padding: "0 8px",
			},
		},
		renderBottomToolbar: () => (
			<Box sx={{ p: 1, display: "flex", justifyContent: "center" }}>
				<Button onClick={handleSaveClick} disabled={!isDirty}>
					Save
				</Button>
			</Box>
		),
		muiTableContainerProps: { sx: { maxHeight: "unset", overflowY: "visible" } },
		columnOrder: ["year", "requiredAnnualCII", "preCorrectionAttainedCII", "attainedAnnualCII", "carbonIntensityRating"],
	});

	return (
		<div>
			<Box sx={{ display: "flex", mb: 2, mt: 2 }}>
				<FormControl size="small">
					<InputLabel>Select Year</InputLabel>
					<Select
						sx={{
							minWidth: 100,
							maxWidth: 200,
							backgroundColor: "white",
							borderRadius: "4px",
							"& .MuiSelect-select": {
								padding: "8px 16px",
							},
						}}
						value={selectedBaseYear}
						label="Base Year"
						onChange={(e) => setSelectedBaseYear(Number(e.target.value))}
						MenuProps={{
							disableScrollLock: true,
							PaperProps: {
								style: {
									maxHeight: 300,
								},
							},
						}}
					>
						{availableYears.map((y) => (
							<MenuItem key={y} value={y}>
								{y}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>
			<MaterialReactTable table={table} />

			<div className="confirmation-modal-container">
				<Modal
					isOpen={openConfirmDialog}
					modalHeader
					padding={"70px"}
					headerHeight={"50px"}
					showFooter
					onCloseClickListener={() => {
						setOpenConfirmDialog(false);
					}}
					overlayStyle={{ zIndex: 2000 }}
					contentStyle={{
						position: "fixed",
						left: 0,
						right: 0,
						top: 0,
						bottom: 0,
						backgroundColor: "rgba(0, 0, 0, 0.5)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
					footer={
						<div className="d-flex justify-content-end fixed-action-btns p-4">
							<Button
								className="modal-delete-btn"
								onClick={handleConfirmSave}
								primaryColor={COLORS.BLUE.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
							>
								Yes
							</Button>
							<Button className="modal-cancel-btn" onClick={handleCancelSave}>
								No
							</Button>
						</div>
					}
				>
					<div>
						{" "}
						Changing the base year will clear the <strong>Section 4(III)</strong> table data,{" "}
						<p style={{ textAlign: "center" }}>Are you sure you want to continue?</p>
					</div>
				</Modal>
			</div>
		</div>
	);
};

export default CiiTargetsActuals;
